import { useEffect, useRef, useState } from "react";

const EMPTY = [];
const COUNTDOWN_MS = 1000;
const LEG_PAUSE_MS = 1800;
const GAME_PAUSE_MS = 3500;
const VISIT_MS = 70;
const LEG_START_MS = 350;
const HIGHLIGHT_MS = 280;
const VISIBLE_LINES = 55;

function isIntro(ev) {
  return ev?.type === "game-intro" || ev?.type === "singles" || ev?.type === "doubles";
}

function delayAfter(ev) {
  if (!ev) return VISIT_MS;
  if (isIntro(ev)) return 0;
  if (ev.type === "leg-end") return LEG_PAUSE_MS;
  if (ev.type === "result") return GAME_PAUSE_MS;
  if (ev.type === "leg-start") return LEG_START_MS;
  if (
    ev.highlight === "maximum" ||
    ev.highlight === "highFinish" ||
    ev.highlight === "ton70_plus" ||
    ev.checkout
  ) {
    return HIGHLIGHT_MS;
  }
  return VISIT_MS;
}

function shortStats(player) {
  if (!player) return "";
  const bits = [player.scoring, player.finishing, player.nerves].filter((v) => v || v === 0);
  const form = player.formLabel ? String(player.formLabel).replace(/ Form$/i, "").toLowerCase() : "";
  if (!bits.length) return form;
  return `${bits.join(" · ")}${form ? ` · ${form}` : ""}`;
}

/**
 * playKey: stabile Session-ID vom Parent — Reset nur bei neuem Match, nicht bei Re-Renders.
 */
const MatchTicker = ({
  events,
  live,
  playKey = "default",
  mySide = "home",
  homeLabel = "Heim",
  awayLabel = "Gast",
  onComplete,
}) => {
  const all = events?.length ? events : EMPTY;
  const [count, setCount] = useState(live ? 1 : all.length);
  const [countdown, setCountdown] = useState(null);
  const scroller = useRef(null);
  const doneRef = useRef(false);
  const skipWaitRef = useRef(false);
  const genRef = useRef(0);

  // Nur bei neuer Play-Session zurücksetzen — nicht wenn `events` dieselbe Datenlage mit neuer Referenz hat.
  useEffect(() => {
    genRef.current += 1;
    doneRef.current = false;
    skipWaitRef.current = false;
    setCountdown(null);
    setCount(live ? Math.min(1, all.length) : all.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- absichtlich nur playKey/live
  }, [playKey, live]);

  useEffect(() => {
    if (!live || count >= all.length) return undefined;
    const gen = genRef.current;
    const justShown = all[count - 1];

    if (isIntro(justShown) && countdown == null) {
      skipWaitRef.current = false;
      setCountdown(3);
      return undefined;
    }
    if (countdown != null && countdown > 0) {
      const id = setTimeout(() => {
        if (genRef.current !== gen) return;
        setCountdown((n) => (n == null ? n : n - 1));
      }, COUNTDOWN_MS);
      return () => clearTimeout(id);
    }
    if (countdown === 0) {
      const id = setTimeout(() => {
        if (genRef.current !== gen) return;
        setCountdown(null);
        setCount((c) => c + 1);
      }, 280);
      return () => clearTimeout(id);
    }

    const wait = skipWaitRef.current ? 40 : delayAfter(justShown);
    skipWaitRef.current = false;
    const id = setTimeout(() => {
      if (genRef.current !== gen) return;
      setCount((c) => c + 1);
    }, wait);
    return () => clearTimeout(id);
  }, [live, count, all, countdown]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [count, countdown]);

  useEffect(() => {
    if (!all.length) {
      if (live && !doneRef.current) {
        doneRef.current = true;
        onComplete?.();
      }
      return;
    }
    const finished = !live || count >= all.length;
    if (finished && !doneRef.current) {
      doneRef.current = true;
      onComplete?.();
    }
  }, [live, count, all.length, onComplete]);

  const shown = all.slice(0, Math.max(0, count));
  const visible = shown.length > VISIBLE_LINES ? shown.slice(shown.length - VISIBLE_LINES) : shown;
  const intro = [...shown].reverse().find(isIntro);
  const finishedGames = shown.filter((ev) => ev.type === "result");
  const latestResult = [...shown].reverse().find((ev) => ev.type === "result");
  const myLatestWin =
    latestResult &&
    latestResult.type === "result" &&
    count < all.length &&
    latestResult.side === mySide;
  const lineClass = (ev) => {
    if (ev.highlight === "maximum" || ev.highlight === "highFinish" || ev.highlight === "ton70_plus") {
      return "text-yellow-300 font-bold";
    }
    if (ev.checkout) return "text-yellow-200 font-semibold";
    if (ev.side === mySide) return "text-emerald-300";
    if (ev.side === "home" || ev.side === "away") return "text-orange-300";
    return "text-gray-400";
  };

  const skipWait = () => {
    // Alte Timer ungültig machen, sonst +1 vom Timeout und +1 vom Klick = Sprünge/Reset-Gefühl
    genRef.current += 1;
    skipWaitRef.current = true;
    if (countdown != null) {
      setCountdown(null);
      setCount((c) => Math.min(all.length, c + 1));
      return;
    }
    setCount((c) => Math.min(all.length, c + 1));
  };

  const skipAll = () => {
    genRef.current += 1;
    skipWaitRef.current = true;
    setCountdown(null);
    setCount(all.length);
  };

  const kindLabel = intro?.kind === "doubles" || intro?.type === "doubles" ? "Doppel" : "Einzel";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 text-xs font-semibold">
        <span className="px-2 py-1 rounded bg-emerald-700 text-white">
          Dein Team · {mySide === "home" ? homeLabel : awayLabel}
        </span>
        <span className="px-2 py-1 rounded bg-orange-700 text-white">
          Gegner · {mySide === "home" ? awayLabel : homeLabel}
        </span>
      </div>

      {live && intro ? (
        <div className="rounded-xl border border-yellow-400 bg-gray-900 text-white p-4">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">
            Partie {intro.gameIndex || finishedGames.length + 1}
            {intro.gamesTotal ? ` / ${intro.gamesTotal}` : ""} · {kindLabel} {intro.slot || ""}
          </div>
          <div className="font-bold text-lg">{intro.home?.name || intro.text}</div>
          {intro.home ? <div className="text-sm opacity-80">{shortStats(intro.home)}</div> : null}
          {intro.away ? (
            <>
              <div className="text-xs opacity-60 my-1">gegen</div>
              <div className="font-bold text-lg">{intro.away.name}</div>
              <div className="text-sm opacity-80">{shortStats(intro.away)}</div>
            </>
          ) : null}
          {countdown != null ? (
            <div className="mt-3 text-3xl font-black text-yellow-300">
              {countdown > 0 ? `Beginnt in ${countdown}` : "Los!"}
            </div>
          ) : null}
        </div>
      ) : null}

      {live ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={skipWait} className="px-3 py-1 text-sm rounded-lg bg-gray-200 dark:bg-gray-700">
            Weiter
          </button>
          <button type="button" onClick={skipAll} className="px-3 py-1 text-sm rounded-lg bg-gray-900 text-white">
            Ticker zu Ende
          </button>
        </div>
      ) : null}

      {live && myLatestWin ? (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-500 text-white p-4 shadow-lg animate-pulse">
          <div className="text-xs uppercase tracking-wide opacity-80">Victory Moment</div>
          <div className="text-2xl font-black">🏆 Punkt für dich!</div>
          <div className="text-sm opacity-90">Kurze Pause, dann geht der Spieltag weiter.</div>
        </div>
      ) : null}

      <div
        ref={scroller}
        className="bg-black text-green-300 font-mono text-sm rounded-lg p-4 max-h-[420px] overflow-y-auto space-y-1"
      >
        {shown.length > VISIBLE_LINES ? (
          <div className="text-gray-500 text-xs mb-1">… {shown.length - VISIBLE_LINES} frühere Würfe</div>
        ) : null}
        {visible.map((ev, i) => {
          const absIndex = shown.length - visible.length + i;
          return (
            <div key={`${playKey}-${absIndex}`} className={lineClass(ev)}>
              {isIntro(ev) && countdown != null && absIndex === shown.length - 1
                ? `${ev.text} — beginnt in ${countdown > 0 ? countdown : "…"}`
                : `${ev.side === mySide ? "► " : ev.side === "home" || ev.side === "away" ? "  " : ""}${ev.text}`}
            </div>
          );
        })}
      </div>

      {finishedGames.length ? (
        <div className="text-sm rounded-lg bg-white dark:bg-gray-700 p-3">
          <div className="font-semibold mb-1">Bisher</div>
          <ul className="space-y-0.5">
            {finishedGames.map((ev, i) => (
              <li key={`${ev.text}-${i}`}>{ev.text}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default MatchTicker;
